<template>
  <div
    ref="nodeRef"
    class="max-w-full text-base font-normal wrap-break-word"
    :class="cssClasses"
    :style="nodeStyle"
  >
    <error-node-wrapper v-if="hasError" :node="node">
      <component
        :is="nodeComponent"
        :node="node"
        :disabled="disabled"
        :data-cy="node.type + index"
        class="text-error"
        @context:apply="applyContext"
      />
    </error-node-wrapper>
    <component
      :is="nodeComponent"
      v-else
      :node="node"
      :disabled="disabled"
      :data-cy="node.type + index"
      @context:apply="applyContext"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, ref } from "vue";

import { EditorNodeName } from "@/enums/customAttributes";
import { isWhitespaceNode } from "@/helpers/parser/formatter";

import AttributeOption from "@/components/customAttributesV2/AttributeOption.vue";
import ErrorNodeWrapper from "@/components/customAttributesV2/ErrorNodeWrapper.vue";
import FormulaBracket from "@/components/customAttributesV2/FormulaBracket.vue";
import FormulaFunction from "@/components/customAttributesV2/FormulaFunction.vue";
import FormulaOperator from "@/components/customAttributesV2/FormulaOperator.vue";
import FormulaService from "@/components/customAttributesV2/FormulaService.vue";
import FormulaUnrecognisedItem from "@/components/customAttributesV2/FormulaUnrecognisedItem.vue";
import RegexEditorNode from "@/components/customAttributesV2/RegexEditorNode.vue";
import Text from "@/components/customAttributesV2/Text.vue";

import type { EditorNode as EditorNodeType } from "@/models/TextEditor";
import type { IContextSetValueModel } from "@/models/orchestration";
import type { Component } from "vue";

const INDENT_PX = 16;
const INDENT_SIZE = 4;

const NODE_COMPONENTS_MAP: Record<EditorNodeName, Component> = markRaw({
  [EditorNodeName.attribute]: FormulaService,
  [EditorNodeName.function]: FormulaFunction,
  [EditorNodeName.operator]: FormulaOperator,
  [EditorNodeName.unrecognized]: FormulaUnrecognisedItem,
  [EditorNodeName.separator]: Text,
  [EditorNodeName.number]: Text,
  [EditorNodeName.parenthesis]: FormulaBracket,
  [EditorNodeName.uncategorized]: Text,
  [EditorNodeName.regex]: RegexEditorNode,
  [EditorNodeName.attributeOption]: AttributeOption,
  [EditorNodeName.whitespace]: Text,
});

interface EditorNodeProps {
  previousNode?: EditorNodeType;
  index: number;
  isPrettyMode?: boolean;
  disabled?: boolean;
}

const {
  previousNode,
  index,
  isPrettyMode = false,
  disabled = false,
} = defineProps<EditorNodeProps>();

const node = defineModel<EditorNodeType>({ required: true });

const applyContext = (context: IContextSetValueModel) => {
  node.value = { ...node.value, context };
};

const nodeRef = ref<HTMLElement | null>(null);

const getIndentFromWhitespace = (content: string): number => {
  const match = content.match(/\n(\s*)/);
  if (!match) {
    return 0;
  }
  return match[1].length;
};

const cssClasses = computed(() => {
  if (!isPrettyMode) {
    return {} as Record<string, boolean>;
  }

  if (isWhitespaceNode(node.value) && node.value.content?.includes("\n")) {
    return { "basis-full": true, "h-0": true };
  }

  return {} as Record<string, boolean>;
});

const nodeStyle = computed(() => {
  if (!isPrettyMode || !previousNode) {
    return {} as Record<string, string>;
  }

  if (
    !isWhitespaceNode(previousNode) ||
    !previousNode.content?.includes("\n")
  ) {
    return {} as Record<string, string>;
  }

  const indentSpaces = getIndentFromWhitespace(previousNode.content);
  const indentLevel = Math.floor(indentSpaces / INDENT_SIZE);

  if (indentLevel > 0) {
    return { "margin-left": `${indentLevel * INDENT_PX}px` };
  }

  return {} as Record<string, string>;
});

const hasError = computed(() => !!node.value.error?.message);

const nodeComponent = computed(() => NODE_COMPONENTS_MAP[node.value.type]);

defineExpose({
  $el: nodeRef,
  getElement: () => nodeRef.value,
});
</script>
